import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Form from '@nubank/nuds-web/components/Form/Form';
import Box from '@nubank/nuds-web/components/Box/Box';

import { useSiteContext } from '../SiteContext/SiteContext';
import { emailOnHeroRegisterEvent } from '../../screens/Registration/tracking';
import { ERROR_SEVERITY, sentryException } from '../../utils/sentry';
import { getDiscoveryUrls } from '../../domains/discoveryUrls/discoveryUrls';
import { STAGE_NAMES } from '../../screens/Registration/stages';
import { trackStageManually } from '../../utils/manualStageTracking';

import {
  StyledTypographyCTA,
  CustomBox,
  StyledTypography,
  StyledBox,
  StyledTextField,
  StyledButton,
  ErrorMessage,
} from './Formbox.styles';

const emailPattern = /^([a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{1,})$/;

const DEFAULT_BUTTON_LABEL = 'FORMBOX.DEFAULT.BUTTON_LABEL';
const DEFAULT_CTA_LABEL = 'FORMBOX.DEFAULT.CTA_LABEL';

function FormBox({
  buttonBackgroundColor,
  buttonLabel,
  ctaColor,
  ctaFontSize,
  ctaGradient,
  ctaHide,
  ctaLabel,
  ctaMarginBottom,
  ctaMaxWidth,
  marginTop,
  boxShadow,
}) {
  const {
    setEmail,
    openRegistrationForm,
    discoveryUrlsList,
    updateDiscoveryUrlsList,
    emailInputFormBox,
    updateIsFormBoxAvailable,
  } = useSiteContext();

  // Get date info for tracking stages manually
  const manualEnteredAt = new Date().toISOString();

  // Display error message if email is invalid
  const [emailValue, setEmailValue] = useState('');
  const [pressedSend, setPressedSend] = useState(false);
  const [displayError, setDisplayError] = useState(false);

  const validateEmail = email => emailPattern.test(email);

  const startMailValidation = () => {
    if (pressedSend) {
      return;
    }
    setPressedSend(true);
    setDisplayError(!validateEmail(emailValue));
  };

  useEffect(() => {
    if (pressedSend) {
      setDisplayError(!validateEmail(emailValue));
    }
  }, [emailValue]);

  // This is used to determine if it should scroll to the form box
  // or open the registration form directly
  useEffect(() => {
    updateIsFormBoxAvailable(true);
  }, [updateIsFormBoxAvailable]);

  const handleSubmit = async ({
    values,
    setSubmitting,
  }) => {
    if (!values.email) {
      setSubmitting(false);
      return;
    }

    try {
      if (!discoveryUrlsList) {
        const discoveryUrls = await getDiscoveryUrls();
        updateDiscoveryUrlsList(discoveryUrls);

        trackStageManually({
          stageName: STAGE_NAMES.EMAIL_INPUT_CTA,
          enteredAt: manualEnteredAt,
          discoveryUrlsList: discoveryUrls,
        });
      } else {
        trackStageManually({
          stageName: STAGE_NAMES.EMAIL_INPUT_CTA,
          enteredAt: manualEnteredAt,
          discoveryUrlsList,
        });
      }

      setEmail(values.email);
      openRegistrationForm();
      emailOnHeroRegisterEvent();
    } catch (error) {
      // Network / discovery failure: fail open — a transient error
      // shouldn't lock the registration funnel. We still record the
      // exception and proceed as if op_state were NU0KAY.
      sentryException({
        error,
        flow: 'application_flow',
        checkpoint: 'first discovery urls and track stage',
        namespace: '<FormBox/>',
        level: ERROR_SEVERITY.CRITICAL,
      });

      setEmail(values.email);
      openRegistrationForm();
      emailOnHeroRegisterEvent();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box width="fit-content">
      <Form>
        <Form.Step
          initialValues={{ email: '' }}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {() => (
            <Box
              display="flex"
              flexDirection="column"
              alignItems={{ xs: 'center', lg: 'flex-start' }}
            >
              {ctaHide ? null : (
                <StyledTypographyCTA
                  intlKey={ctaLabel}
                  variant={ctaFontSize}
                  color={ctaColor}
                  ctaGradient={ctaGradient}
                  marginBottom={ctaMarginBottom}
                  marginTop={{ xs: '0', md: '4x', lg: '0' }}
                  textAlign={{ xs: 'center', lg: 'left' }}
                  maxWidth={ctaMaxWidth}
                  strong
                />
              )}

              <Box width="100%">
                <CustomBox
                  backgroundColor="white.default"
                  width={{ xs: '300px', md: '400px' }}
                  height={{ xs: '130px', md: '140px', lg: '136px' }}
                  marginTop={marginTop}
                  borderRadius="24px"
                  paddingHorizontal="18px"
                  paddingTop="16px"
                  boxShadow={boxShadow ? '0px 0px 20px 0px #e0c2ffab' : 'none'}
                >

                  <StyledTypography
                    intlKey={buttonLabel}
                    marginTop={{ xs: '0', md: '4x', lg: '0' }}
                    textAlign="left"
                    strong
                  />

                  <StyledBox
                    display="flex"
                    flexDirection="column"
                    position="relative"
                    width="100%"
                  >
                    <StyledTextField
                      id="email-on-hero"
                      ref={emailInputFormBox}
                      type="email"
                      name="email"
                      className="email-1"
                      label=""
                      placeholder="Escribe tu correo electrónico"
                      syncValidations={{
                        required: 'Revisa que tu correo esté bien',
                        email: 'Revisa que tu correo esté bien',
                      }}
                      onInput={e => setEmailValue(e.target.value)}
                    />

                    <StyledButton
                      id="email-on-hero-submit-btn"
                      variant="contained"
                      type="submit"
                      size="small"
                      buttonBackgroundColor={buttonBackgroundColor}
                      iconProps={{ name: 'chevron-right', title: 'Continuar' }}
                      onClick={() => startMailValidation()}
                    />

                  </StyledBox>
                  {displayError && (
                    <Box>
                      <ErrorMessage variant="paragraph1">
                        Revisa que tu correo esté bien
                      </ErrorMessage>
                    </Box>
                  )}
                </CustomBox>
              </Box>
            </Box>
          )}
        </Form.Step>
      </Form>
    </Box>
  );
}

FormBox.defaultProps = {
  boxShadow: false,
  buttonBackgroundColor: 'default',
  buttonLabel: DEFAULT_BUTTON_LABEL,
  ctaColor: 'white',
  ctaFontSize: 'heading4',
  ctaGradient: 'default',
  ctaHide: false,
  ctaLabel: DEFAULT_CTA_LABEL,
  ctaMarginBottom: '2x',
  ctaMaxWidth: 'auto',
  marginTop: '24px',
};

FormBox.propTypes = {
  boxShadow: PropTypes.bool,
  buttonBackgroundColor: PropTypes.string,
  buttonLabel: PropTypes.string,
  ctaColor: PropTypes.string,
  ctaFontSize: PropTypes.string,
  ctaGradient: PropTypes.string,
  ctaHide: PropTypes.bool,
  ctaLabel: PropTypes.string,
  ctaMarginBottom: PropTypes.string,
  ctaMaxWidth: PropTypes.string,
  marginTop: PropTypes.string,
};

export default FormBox;

